<template>
  <div class="hc-hexabubble">
    <svg :style="style" class="hc-hexabubble-chart" :opacity="opacity" @click="getParent" ref="bubble">
      <complexagon
        v-for="(d, index) in Data"
        :key="index"
        :id="d.id || index"
        :scale="d.sd * scale"
        :curve=".8"
        :x="d.x * scale"
        :y="d.y * scale"
        :offColor="interpolateOrGr(d.variation)"
        :text="d.name"
        :textValue="parseInt(d.d) + ' ' + unit"
        :hasChildren="d.children && d.children.length > 0"
        :isDraggable="true"
        @hc-click="click"
        @hc-hover="hover"
        @hc-hoverend="hoverend"
        @hc-drag="drag"
        @hc-dragstart="dragstart"
        @hc-dragend="dragend"
      />
      <color-legend :scale="colorScale" :h="legendTileHeight - 1" :x="width - legendWidth / 2" :y="height / 2 - 3.5 * legendTileHeight"/>
    </svg>
  </div>
</template>

<script>
import infoBus from '../libraries/infoBus'
import tween from '../libraries/tween'
import Complexagon from '../combo/Complexagon'
import { scaleOrdinal, scalePow } from 'd3-scale'
import { extent } from 'd3-array'
import force from '../libraries/force'
import ColorLegend from '../libraries/ColorLegend'
export default {
  name: 'HexaBubbuleChart',
  components: {
    Complexagon,
    ColorLegend
  },
  props: {
    data: {
      type: Array,
      default () { return []}
    },
    unit: {
      type: String,
      default: 'MD'
    },
    legendWidth: {
      type: Number,
      default: 40
    },
    legendTileHeight: {
      type: Number,
      default: 30
    },
    animation: {
      type: Boolean,
      default: false
    },
    animationDuration: {
      type: Number,
      default: 1000
    },
    animationOptions: {
      type: Object,
      default () {
        return {
          delay: 0,
          easing: 'Circular',
          easingEdge: 'In'
        }
      }
    },
    rangeScale: {
      type: Array,
      default () { return [3, 6]}
    }
  },
  data () {
    return {
      opacity: 1,
      interact: true,
      width: 1000,
      height: 500,
      Data: [],
      simulation: {}
    }
  },
  mounted () {
    this.resize()
    window.addEventListener('resize', this.resize)
    let X = this.width / 2 - this.legendWidth
    let Y = 0
    this.simulation = force (this.data.map((d, index) => {
      const x = X
      const y = Y
      if ((X + this.scaleLog(d.d) * 32) > (this.width - this.legendWidth)) {
        X = Y + this.scaleLog(d.d) * 32
        Y = Y + this.scaleLog(d.d) * 32
      } else {
        X = X + this.scaleLog(d.d) * 32
      }

      return {
        d: d.d,
        sd: this.scaleLog(d.d),
        id: d.id,
        index,
        x,
        y,
        vx: 10,
        vy: 10,
        name: d.name,
        children: d.children,
        variation: d.variation,
        parent: d.parent,
        detailedBudget: d.detailedBudget,
        link: d.link
      }
    }), this.width / 2 - this.legendWidth, this.height / 2, this.scale, (data) => {
      this.Data = data
      // this.simulation.stop()
    })
  },
  methods: {
    getParent (e) {
      if (e.target === this.$refs.bubble) {
        if(this.Data[0] && this.Data[0].parent) {
          let target = this.data
          if (this.Data[0].parent.parent) {
            target = this.Data[0].parent.parent.children
          }
          if (this.animation) {
            this.interact = false
            tween(
              {sd: (this.width - this.legendWidth) / 16, opacity: 0},
              {sd: this.Data[0].sd, opacity: 1},
              this.animationDuration,
              (cell) => {
                this.Data[0].sd = cell.sd
                this.opacity = cell.opacity
                this.simulation.nodes(this.Data).resume()
              }, this.animationOptions, () => {
                const x = this.Data[0].x
                const y = this.Data[0].y
                this.Data = target.map((d, index) => {
                return {
                  d: d.d,
                  sd: this.scaleLog(d.d),
                  id: d.id,
                  index,
                  x: x + this.scaleLog(d.d) * index * 2,
                  y,
                  vx: 10,
                  vy: 10,
                  name: d.name,
                  children: d.children,
                  variation: d.variation,
                  parent: d.parent,
                  detailedBudget: d.detailedBudget,
                  link: d.link
                }})
                this.simulation.stop()
                this.simulation = force (this.Data, this.width / 2 - this.legendWidth, this.height / 2, this.scale, (data) => {
                  this.Data = data
                })
                tween(1, .7, this.animationDuration / 4, (opacity) => {
                  this.opacity = opacity
                }, this.animationOptions)
                this.interact = true
              })
          } else {
            const x = this.Data[0].x
            const y = this.Data[0].y
            this.Data = target.map((d, index) => {
              return {
                d: d.d,
                sd: this.scaleLog(d.d),
                id: d.id,
                index,
                x: x + this.scaleLog(d.d) * index * 50,
                y,
                vx: 10,
                vy: 10,
                name: d.name,
                children: d.children,
                variation: d.variation,
                parent: d.parent,
                detailedBudget: d.detailedBudget,
                link: d.link
              }})
              this.simulation.stop()
              this.simulation = force (this.Data, this.width / 2 - this.legendWidth, this.height / 2, this.scale, (data) => {
                this.Data = data
              })
              this.simulation.resume()
          }
        }
      }
    },
    resize () {
      this.width = this.$el.offsetWidth
      this.height = this.$el.offsetHeight
      if (this.simulation.stop) {
        this.simulation.resize(this.width / 2 - this.legendWidth, this.height / 2,  this.scale)
      }
    },
    getScale (value) {
      return this.scale * this.scaleLog(value)
    },
    click (e, id) {
      const node = this.Data.find(d => d.id === id)
      if (node.children && node.children.length && this.interact) {
        if (this.animation) {
          this.interact = false
          tween(
            {sd: (this.width - this.legendWidth) / 16, opacity: 0},
            {sd: node.sd, opacity: 1},
            this.animationDuration,
            (cell) => {
              node.sd = cell.sd
              this.opacity = cell.opacity
              this.simulation.nodes(this.Data).resume()
            }, this.animationOptions, () => {
              const x = node.x
              const y = node.y
              this.Data = node.children.map((d, index) => {
              return {
                d: d.d,
                sd: this.scaleLog(d.d),
                id: d.id,
                index,
                x: x + this.scaleLog(d.d) * index * 2,
                y,
                vx: 10,
                vy: 10,
                name: d.name,
                children: d.children,
                variation: d.variation,
                parent: d.parent,
                detailedBudget: d.detailedBudget,
                link: d.link
              }})
              this.simulation.stop()
              this.simulation = force (this.Data, this.width / 2 - this.legendWidth, this.height / 2, this.scale, (data) => {
                this.Data = data
              })
              tween(1, .7, this.animationDuration / 4, (opacity) => {
                this.opacity = opacity
              }, this.animationOptions)
              this.interact = true
            })
        } else {
          const x = node.x
          const y = node.y
          this.Data = node.children.map((d, index) => {
            return {
              d: d.d,
              sd: this.scaleLog(d.d),
              id: d.id,
              index,
              x: x + this.scaleLog(d.d) * index * 50,
              y,
              vx: 10,
              vy: 10,
              name: d.name,
              children: d.children,
              variation: d.variation,
              parent: d.parent,
              detailedBudget: d.detailedBudget,
              link: d.link
            }})
            this.simulation.stop()
            this.simulation = force (this.Data, this.width / 2 - this.legendWidth, this.height / 2, this.scale, (data) => {
              this.Data = data
            })
            this.simulation.resume()
        }
      }
    },
    hover (e, id) {
      const d = this.Data.find(d => d.id === id)
      const info = {
        title: d.name,
        body: (d.detailedBudget && d.variation != null) ? `<p><b>BUDGET</b>: ${d.detailedBudget}</p><p><b>VARIATION</b>: <span style="color: ${d.variation > 0 ? 'green' : '#f56043'}">${d.variation > 0 ? '+' : ''}${d.variation}%</span></p>` : '',
        link: d.link
      }
      infoBus.$emit('hc-info-show', e, info)
    },
    hoverend (e) {
      infoBus.$emit('hc-info-hide')
    },
    drag (e, id) {
      const data = this.Data.find(d => d.id === id)
      data.x = e.dragX / this.scale
      data.y = e.dragY / this.scale
    },
    dragstart (e) {
      this.simulation.stop()
    },
    dragend (e) {
      this.simulation.resume()
    },
    interpolateOrGr (i) {
      i = i >= 20 ? 19 : i <= -15 ? -14 : i 
      return this.colorScale(Math.floor((i + 15) / 5))
    }
  },
  watch: {
    data (newData , oldData) {
      let X = this.width / 2 - this.legendWidth
      let Y = 0
      this.simulation.stop()
      this.simulation = force (this.data.map((d, index) => {
        const x = X
        const y = Y
        if ((X + this.scaleLog(d.d) * 32) > (this.width - this.legendWidth)) {
          X = Y + this.scaleLog(d.d) * 32
          Y = Y + this.scaleLog(d.d) * 32
        } else {
          X = X + this.scaleLog(d.d) * 32
        }

        return {
          d: d.d,
          sd: this.scaleLog(d.d),
          id: d.id,
          index,
          x,
          y,
          vx: 10,
          vy: 10,
          name: d.name,
          children: d.children,
          variation: d.variation,
          parent: d.parent,
          detailedBudget: d.detailedBudget,
          link: d.link
        }
      }), this.width / 2 - this.legendWidth, this.height / 2, this.scale, (data) => {
        this.Data = data
        // this.simulation.stop()
      })
    }
  },
  computed: {
    style () {
      return { height: `${this.height}px`, width: `${this.width}px`, overflow: 'visible' }
    },
    scale () {
      return Math.sqrt(this.width / 500 * this.height / 500)
    },
    scaleLog () {
      // const max = this.data.reduce((a, b) => a + b.d, 0)
      return scalePow().exponent(1.2)
        .range(this.rangeScale)
        .domain(extent(this.data, d => d.d))
    },
    colorScale () {
      return scaleOrdinal()
        .range(['#f56043', '#faa795', '#fcc3b8', '#bbb', '#999', '#666', '#333'])
        .domain([0, 1, 2, 3, 4, 5, 6])
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resize)
  }
}
</script>

<style>
</style>
